import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  cadenciaParaLead,
  descrever,
  ehPosComparecimento,
  indiceDoPasso,
  proximaData,
  proximoPasso,
} from '@/leads/cadencia';
import {
  TIPOS_TAREFA,
  concluirTarefa,
  criarTarefa,
  ouvirTarefasDoLead,
} from '@/services/tarefasService';

/** Formato aceito por <input type="datetime-local">. */
const paraInput = (data) => format(data, "yyyy-MM-dd'T'HH:mm");

const exibir = (valor) => {
  const data = typeof valor?.toDate === 'function' ? valor.toDate() : valor;
  return data ? format(data, 'dd/MM/yy HH:mm') : '—';
};

/**
 * Follow-ups do lead dentro do painel: pendentes, concluídos e agendamento novo.
 *
 * `clinicaId`, `autor` e `cadencia` vêm por PROPS (V2): o bloco também vive no
 * painel da conversa, fora do `LeadsProvider`. O `PainelLead` repassa o que lê
 * do contexto; o `PainelContato` repassa o que lê do `ConversasProvider`.
 */
export default function BlocoTarefas({ lead, clinicaId, autor, clinica }) {
  const [tarefas, setTarefas] = useState([]);
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  /**
   * V4: a cadência depende da ETAPA. Quem compareceu e não fechou está na régua
   * de retorno (1d/3d/7d/15d), e o degrau é contado em retornos concluídos —
   * não em ligações registradas. Nas demais etapas, a régua de primeiro contato
   * por `tentativasContato`, como sempre.
   */
  const passos = cadenciaParaLead(lead, clinica?.config);
  const indice = indiceDoPasso(lead, tarefas);
  const sugestao = useMemo(() => proximaData(indice, passos), [indice, passos]);
  const passoSugerido = proximoPasso(indice, passos);
  const tipoSugerido = ehPosComparecimento(lead.etapa) ? 'retorno' : 'ligacao';

  const [quando, setQuando] = useState('');
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState('ligacao');
  // ★ WT 1b: falha da carga aparece aqui — nunca vira "Nenhum contato pendente".
  const [erroCarga, setErroCarga] = useState(null);

  /**
   * ★★★ WT lote 1b — falha de carga **nunca** vira "Nenhum contato pendente".
   *
   * ⚠⚠ Era `console.error` e nada mais: com a consulta recusada, o bloco afirmava que o lead
   * **não tem follow-up** — e é o contrário do que a SDR precisa saber. ★ Mesmo molde que a
   * **VQ-correção 1** já aplicou no `BlocoAgendamento` ao lado.
   */
  useEffect(() => {
    setTarefas([]);
    setErroCarga(null);
    return ouvirTarefasDoLead(
      clinicaId, lead.id,
      (lista) => { setErroCarga(null); setTarefas(lista); },
      () => setErroCarga('Não foi possível carregar os follow-ups. Recarregue a página. Se continuar, avise o suporte.'),
    );
  }, [clinicaId, lead.id]);

  function abrirFormulario() {
    setQuando(paraInput(sugestao ?? new Date(Date.now() + 3_600_000)));
    setTitulo('');
    setTipo(tipoSugerido);
    setErro(null);
    setAbrindo(true);
  }

  async function agendar(evento) {
    evento.preventDefault();
    setErro(null);
    setOcupado(true);
    try {
      await criarTarefa(
        clinicaId,
        lead,
        { titulo, tipo, dataHora: new Date(quando) },
        autor,
      );
      setAbrindo(false);
    } catch (e) {
      setErro(e.message ?? 'Não foi possível agendar o contato.');
    } finally {
      setOcupado(false);
    }
  }

  async function concluir(tarefa) {
    setErro(null);
    setOcupado(true);
    try {
      await concluirTarefa(clinicaId, { ...tarefa, leadId: lead.id }, autor);
    } catch (e) {
      setErro(e.message ?? 'Não foi possível concluir o contato.');
    } finally {
      setOcupado(false);
    }
  }

  const pendentes = tarefas.filter((t) => !t.concluida);

  return (
    <section className="bloco-tarefas">
      {/* ★ VV-c2 (decisão do dono, 20/set/2026): "Próximo contato" / "Agendar contato" —
          "FOLLOW-UPS · Agendar" ao lado do "Agendar" de Consultas era lido como agendar
          consulta. Só rótulo; a seção "Follow-up" de Próximos contatos mantém o nome. */}
      <div className="bloco-titulo">
        <h3>Próximo contato</h3>
        <button type="button" className="btn-secundario" onClick={abrirFormulario}>
          Agendar contato
        </button>
      </div>

      {erroCarga && <p className="erro" role="alert">{erroCarga}</p>}
      {/* ⚠ `!erroCarga` é o que impede a frase de afirmar ausência sobre uma carga que falhou */}
      {pendentes.length === 0 && !abrindo && !erroCarga && (
        <p className="vazio compacto">
          Nenhum contato pendente.
          {passoSugerido
            ? ` Cadência sugere daqui a ${descrever(passoSugerido)}.`
            : ' A cadência se esgotou — decida entre trabalhar manualmente ou registrar o motivo (Não agendou / Não fechou).'}
        </p>
      )}

      <ul className="lista-tarefas">
        {pendentes.map((t) => (
          <li key={t.id}>
            <div>
              <strong>{t.titulo}</strong>
              <span className="tarefa-quando">{exibir(t.dataHora)}</span>
            </div>
            <button
              type="button"
              className="btn-secundario"
              disabled={ocupado}
              onClick={() => concluir(t)}
            >
              Concluir
            </button>
          </li>
        ))}
      </ul>

      {abrindo && (
        <form className="form-tarefa" onSubmit={agendar}>
          <div className="campo">
            <label htmlFor="bt-titulo">O que fazer</label>
            <input
              id="bt-titulo"
              placeholder={passoSugerido ? `Próximo contato (${descrever(passoSugerido)})` : 'Próximo contato'}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
            />
          </div>
          <div className="linha">
            <div className="campo">
              <label htmlFor="bt-tipo">Tipo</label>
              <select id="bt-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                {TIPOS_TAREFA.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="bt-quando">Quando</label>
              <input
                id="bt-quando"
                type="datetime-local"
                required
                value={quando}
                onChange={(e) => setQuando(e.target.value)}
              />
            </div>
          </div>
          <div className="dialogo-acoes">
            <button type="button" className="btn-secundario" onClick={() => setAbrindo(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn-primario" disabled={ocupado}>
              Agendar contato
            </button>
          </div>
        </form>
      )}

      {erro && <p className="erro">{erro}</p>}
    </section>
  );
}
